import { useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import Rail from './Rail'
import { MobileTabBar } from './Sidebar'
import { PaywallProvider } from './paywall'
import { SignInProvider } from './SignInModal'
import MobileAppPromo from './MobileAppPromo'
import { broadcastSubscriptionStatus, isSubscriptionActive, seedSubscriptionFromAuth } from '@/lib/baba-api'
import { getAuthToken, isSignedIn, readJwtClaims, setAuthToken } from '@/lib/baba-auth'
import { HANDOFF_FAILED_MESSAGES, readSessionHandoff, takeHandoffFailures, type PendingSession } from '@/lib/sessionHandoff'
import { babaApi, useRestoreSubscriptionMutation, useSubscriptionStatusQuery, useVerifyMagicLinkMutation } from '../store/babaApi'
import { useAppDispatch } from '../store/hooks'
import { captureReferralFromUrl } from '@/lib/referral'
import { useGatewayMe } from '@/lib/gateway'
import { phIdentify } from '@/lib/posthog'
import { getAuthEmail } from '@/lib/baba-auth'
import { identifyAdUser, whenConsented } from '@baba/attribution/ad-pixels'
import { installOutboundLinkTracking, registrationEventId, trackCheckoutReturned, trackLogin, trackSignUp } from '@/lib/analytics'
import { markPhoneLinkPromptPending } from '@/lib/phoneLink'
import { claimEntitlementSyncForSession, gatewayEntitlementLags, syncGatewayEntitlement } from '@/lib/gatewayEntitlement'
import { handAccountLocalStateTo, normalizeAccountOwner, readAccountLocalStateOwner, setAccountLocalStateOwner } from '@/lib/accountLocalState'
import { readPendingJoin } from '@/lib/orgJoin'
import { markAppBusy } from '@/lib/appUpdateCheck'
import PhoneLinkPrompt from './PhoneLinkPrompt'

const CHECKOUT_TOAST_KEY = 'baba.app.checkout_toast_pending'
const CHECKOUT_PENDING_KEY = 'baba.app.checkout_pending'
const EMAIL_CHANGED_TOAST_KEY = 'baba.app.email_changed_toast_pending'
/** Where index.html's inline script parks a Google `#session=…` hand-off. */
const SESSION_CALLBACK_KEY = 'baba_session_callback'

function readSessionItem(key: string): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSessionItem(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value)
  } catch {}
}

function removeSessionItem(key: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(key)
  } catch {}
}

function readMagicLinkParams(searchParams: URLSearchParams): { token: string | null; email: string | null } {
  const hashParams = new URLSearchParams(
    typeof window !== 'undefined' && window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : '',
  )
  const storedParams = new URLSearchParams(readSessionItem('baba_auth_callback') ?? '')
  return {
    token: searchParams.get('token') ?? hashParams.get('token') ?? storedParams.get('token'),
    email: searchParams.get('email') ?? hashParams.get('email') ?? storedParams.get('email'),
  }
}

function cleanMagicLinkUrl(searchParams: URLSearchParams): string {
  const next = new URLSearchParams(searchParams)
  next.delete('token')
  next.delete('email')
  const clean = next.toString()
  return `${window.location.pathname}${clean ? `?${clean}` : ''}`
}

/**
 * The Google hand-off (`session`, `email`, `new`, `email_changed`), taken once
 * and cleared: index.html's inline script moves it out of the fragment into
 * sessionStorage before Meta Pixel, gtag or Sentry can read the URL. The
 * fragment itself is still read, and scrubbed, if that script did not run.
 */
function takeSessionCallback(): URLSearchParams | null {
  const parked = readSessionItem(SESSION_CALLBACK_KEY)
  if (parked) {
    removeSessionItem(SESSION_CALLBACK_KEY)
    return new URLSearchParams(parked)
  }
  if (typeof window === 'undefined' || !window.location.hash.startsWith('#')) return null
  const hash = new URLSearchParams(window.location.hash.slice(1))
  if (!hash.get('session')) return null
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  return hash
}

/** "Continue as <email>?" before any link-carried sign-in is adopted. */
function SignInConfirmDialog({
  email,
  busy,
  onConfirm,
  onCancel,
}: {
  email: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-confirm-title"
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-6 text-app shadow-token-md">
        <h2 id="signin-confirm-title" className="text-lg font-semibold">Sign in to baba</h2>
        <p className="mt-2 text-sm text-muted">
          Continue as <span className="font-medium text-app">{email}</span>?
        </p>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="mt-5 w-full rounded-xl bg-[#c2703a] px-5 py-3 font-semibold text-white transition-all duration-150 hover:brightness-105 active:scale-[0.98] disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="mt-2 w-full rounded-xl px-5 py-2.5 text-sm font-medium text-muted transition-colors duration-150 hover:text-app disabled:opacity-50"
        >
          Not you? Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Resolves this session's gateway identity once per mount and relays its
 * stable_user_id to baba api (see useGatewayMe). This is what lets a phone
 * that links later find the web account already recorded, so the two
 * identities merge instead of drifting apart. It was previously never
 * mounted anywhere, which left web-first accounts split from their phone.
 */
function GatewayIdentitySync() {
  const { data } = useGatewayMe()
  const { data: subscription } = useSubscriptionStatusQuery()
  const stableUserId = data?.stable_user_id
  // The same id is the PostHog person: it survives an email change, it is
  // what the phone reports under, and the anonymous history in this browser
  // (including the landing-site visit) merges into it on first identify.
  useEffect(() => {
    if (!stableUserId) return
    const email = getAuthEmail()
    phIdentify(stableUserId, email ? { email } : undefined)
  }, [stableUserId])
  // Advanced matching for the Meta and TikTok pixels: the normalized email and the platform user
  // id (the session's userId, the id the server's events use as external_id), once the visitor
  // has allowed ad_user_data. The pixels hash them.
  useEffect(() => {
    const token = getAuthToken()
    const userId = token ? readJwtClaims(token)?.userId : undefined
    whenConsented('ad_user_data', () => identifyAdUser({ email: getAuthEmail(), externalId: typeof userId === 'string' ? userId : undefined }))
  }, [])
  // A web subscriber whose gateway row still says Free: everything the
  // gateway enforces (characters, images, PDFs, voice, the tutor, email)
  // treats them as Free until the entitlement is copied across. Once per tab
  // session per identity; see lib/gatewayEntitlement.ts.
  useEffect(() => {
    if (!stableUserId || !data) return
    if (!gatewayEntitlementLags(subscription, data)) return
    if (!claimEntitlementSyncForSession(stableUserId)) return
    void syncGatewayEntitlement({ stableUserId })
  }, [stableUserId, data, subscription])
  return null
}

/**
 * Gentle fade-in on route change. No exit animation: the outgoing
 * page unmounts instantly and the incoming page fades in. This avoids
 * the strobe effect of mode="wait" and the layout jitter of y-translate
 * transforms on a scrollable container. A CSS animation (globals.css
 * `.animate-route-fade`), so the shell carries no animation library.
 */
function RouteTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  // Track whether this is the first render. The initial page load IS the
  // LCP — we must NOT start at opacity: 0 or we add ~140ms of invisible
  // content to FCP/LCP on mobile. Only fade on subsequent route changes.
  const isFirstRender = useRef(true)
  useEffect(() => {
    isFirstRender.current = false
  }, [])
  // Key on the top-level section only — navigating between sub-routes
  // (settings sections, chat conversations) must not remount the page.
  const section = location.pathname.split('/')[1] || 'home'
  return (
    <RouteSection key={section} fade={!isFirstRender.current}>
      {children}
    </RouteSection>
  )
}

function RouteSection({ fade, children }: { fade: boolean; children: React.ReactNode }) {
  // Decided once per mount: a later re-render of the same section (a search
  // param change) must not replay the fade over a page already on screen.
  const [className] = useState(() => (fade ? 'h-full animate-route-fade' : 'h-full'))
  return (
    <div className={className}>
      {/* A page that throws takes down only itself, with a way out; the rail,
          tab bar and every other route keep working. Keyed per section by
          RouteTransition, so moving to another section clears it. */}
      <Sentry.ErrorBoundary fallback={({ resetError }) => <RouteErrorFallback resetError={resetError} />}>
        {children}
      </Sentry.ErrorBoundary>
    </div>
  )
}

function RouteErrorFallback({ resetError }: { resetError: () => void }) {
  const navigate = useNavigate()
  return (
    <div role="alert" className="h-full min-h-[40vh] flex flex-col items-center justify-center gap-3 p-8 text-center">
      <h2 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>This page ran into a problem</h2>
      <p className="max-w-sm text-sm" style={{ color: 'var(--muted)' }}>
        We've been notified. Reload to try again, or go back to Translate.
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="h-10 rounded-full px-5 text-sm font-semibold transition-all duration-150 hover:brightness-105 active:scale-[0.98] cursor-pointer"
          style={{ background: 'var(--accent)', color: 'var(--cta-fg)' }}
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => {
            resetError()
            navigate('/translate')
          }}
          className="h-10 rounded-full px-5 text-sm font-medium transition-colors duration-150 hover:bg-[var(--card-2)] active:scale-[0.98] cursor-pointer"
          style={{ border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)' }}
        >
          Home
        </button>
      </div>
    </div>
  )
}

export default function AppShell() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [verifyMagicLink] = useVerifyMagicLinkMutation()
  const [restoreSubscription] = useRestoreSubscriptionMutation()
  const mainRef = useRef<HTMLElement>(null)
  // True while a checkout return is waiting for the webhook to activate Pro.
  // Seeded from sessionStorage so a reload resumes the wait (see below).
  const [checkoutPending, setCheckoutPending] = useState<boolean>(() => readSessionItem(CHECKOUT_PENDING_KEY) !== null)

  // Mark the document while the app shell is mounted. globals.css uses this to
  // lock html/body scrolling so the shell's own 100dvh height is the ONLY
  // vertical authority — mobile Safari otherwise scrolls the document by the
  // difference between `100vh` and `100dvh` and the bottom nav floats. The
  // public share routes render outside AppShell and keep normal page scroll.
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-app-shell', '')
    return () => root.removeAttribute('data-app-shell')
  }, [])

  // Every link that leaves the app records an outbound_link_clicked (see
  // lib/analytics.ts); autocapture is off, so nothing else would see them.
  useEffect(() => installOutboundLinkTracking(), [])

  // Reset scroll to top on every route change so pages don't open
  // halfway down. Uses the <main> scroll container, not window.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
  }, [location.pathname])

  useEffect(() => {
    if (readSessionItem(CHECKOUT_TOAST_KEY) !== '1') return
    removeSessionItem(CHECKOUT_TOAST_KEY)
    toast.success('Welcome to baba Pro. Every feature is unlocked.', {
      duration: 6000,
    })
  }, [])

  useEffect(() => {
    const changed = readSessionItem(EMAIL_CHANGED_TOAST_KEY)
    if (!changed) return
    removeSessionItem(EMAIL_CHANGED_TOAST_KEY)
    toast.success(`Email updated to ${changed}. Use it next time you sign in.`, { duration: 6000 })
  }, [])

  // Persist ?r=CODE from the URL to localStorage once per session.
  // Runs after the magic-link handler so token=... takes priority — we
  // don't want a logged-in returning user's tab to overwrite their code.
  useEffect(() => {
    captureReferralFromUrl()
  }, [])

  // Google OAuth session handoff. apps/api emits `#session=<jwt>&email=<email>`
  // on app.itsbaba.com after exchanging the Google auth code. Unlike magic
  // link, this is already a session JWT, so /auth/verify is skipped. Runs
  // before the magic-link handler so #session= isn't mistakenly POSTed as a
  // one-time token.
  //
  // Anyone can build such a link, and opening it used to sign the visitor in
  // to whatever account it carried (login CSRF), under the email the LINK
  // named. Now the email comes from the token, and EVERY fragment session asks
  // "Continue as <email>?" first. There is no silent path: nothing on the
  // return trip tells our own Google sign-in apart from a crafted link until
  // apps/api binds a nonce through the OAuth state (follow-up described in
  // lib/googleSignIn.ts).
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(null)

  const adoptSession = (pending: PendingSession) => {
    // The owner marker moves BEFORE the cookie: every open tab shares the
    // cookie, and one still holding the previous account's chats must already
    // refuse to sync them when the new session lands (lib/accountLocalState.ts).
    let carried = false
    if (pending.isEmailChange) {
      // Same account, new address: what this browser holds for it stays its
      // own instead of being cleared as another account's on the next load.
      // Only when the token is for the SAME account id: `email_changed=1` is
      // part of a link anyone can write, and carrying the chats over to a
      // stranger's session would hand them to that account's sync.
      const current = getAuthEmail()
      const currentToken = getAuthToken()
      const currentId = currentToken ? readJwtClaims(currentToken)?.userId : undefined
      const nextId = readJwtClaims(pending.session)?.userId
      const sameAccount = typeof currentId === 'string' && currentId !== '' && currentId === nextId
      if (sameAccount && current && readAccountLocalStateOwner() === normalizeAccountOwner(current)) {
        setAccountLocalStateOwner(pending.email)
        carried = true
      }
    }
    if (!carried) handAccountLocalStateTo(pending.email)
    setAuthToken(pending.session, pending.email)
    if (pending.isEmailChange) {
      // Confirmed email change: fresh session for the new address. The toast
      // fires after the reload via sessionStorage (a pre-reload toast dies).
      writeSessionItem(EMAIL_CHANGED_TOAST_KEY, pending.email)
    } else if (pending.isNewUser) {
      trackSignUp({ email: pending.email, eventId: registrationEventId(pending.eventId, pending.session) })
      markPhoneLinkPromptPending(pending.email)
      toast.success('Signed in. Welcome to baba!')
    } else {
      trackLogin({ email: pending.email })
      markPhoneLinkPromptPending(pending.email)
      toast.success('Signed in. Welcome to baba!')
    }
    window.location.reload()
  }

  useEffect(() => {
    const pending = readSessionHandoff(takeSessionCallback())
    if (!pending) return
    if (pending === 'invalid') {
      toast.error('That sign-in link is not valid. Sign in again.')
      return
    }
    if (pending === 'expired') {
      toast.error('That sign-in link has expired. Sign in again.')
      return
    }
    // Always behind the confirmation; adoptSession runs only from its button.
    setPendingSession(pending)
  }, [])

  // index.html could not hand a URL secret to sessionStorage. It already took
  // the secret out of the address bar and dropped the callback; say so rather
  // than leave the visitor wondering why nothing happened.
  useEffect(() => {
    for (const kind of takeHandoffFailures(window as Window & { __babaAuthHandoffFailed?: unknown })) {
      toast.error(HANDOFF_FAILED_MESSAGES[kind], { duration: 10000 })
    }
  }, [])

  // An organization join link sends a signed-out member through sign-in, and
  // Google and the emailed link both land on "/". JoinPage parked where it
  // was (lib/orgJoin.ts); go back there once signed in.
  useEffect(() => {
    if (!isSignedIn()) return
    const target = readPendingJoin()
    if (target) navigate(target, { replace: true })
  }, [navigate])

  // A magic link is single-use and shares its row with the emailed 6-digit
  // code. Corporate mail filters (Microsoft Safe Links and the like) open
  // every link in an incoming email in a sandbox browser, and this page used
  // to POST /verify on load, so the scanner consumed the link before the
  // person ever saw the email and their code then read "already used". The
  // token now waits behind a button: scanners fetch pages, they do not click.
  const [pendingMagicLink, setPendingMagicLink] = useState<{ token: string; email: string } | null>(null)
  const [magicLinkBusy, setMagicLinkBusy] = useState(false)

  useEffect(() => {
    const { token, email } = readMagicLinkParams(searchParams)
    if (!token || !email) return
    setPendingMagicLink({ token, email })
  }, [location.hash, searchParams])

  const confirmMagicLink = () => {
    if (!pendingMagicLink || magicLinkBusy) return
    const { token, email } = pendingMagicLink
    setMagicLinkBusy(true)
    let cancelled = false

    verifyMagicLink({ email, token })
      .unwrap()
      .then((result) => {
        if (cancelled) return
        if (result.token) {
          // Owner marker first, then the cookie (see adoptSession).
          handAccountLocalStateTo(email)
          setAuthToken(result.token, email)
          const seeded = seedSubscriptionFromAuth({
            subscription_status: result.subscription_status,
            plan: result.plan ?? null,
          })
          broadcastSubscriptionStatus(seeded)
          if (result.is_new_user) {
            trackSignUp({ email, eventId: registrationEventId(result.eventId, result.token) })
          } else {
            trackLogin({ email })
          }
          removeSessionItem('baba_auth_callback')
          markPhoneLinkPromptPending(email)
          toast.success('Signed in. Welcome to baba!')
          window.location.href = cleanMagicLinkUrl(searchParams)
        } else {
          toast.error(result.error || 'That sign-in link expired. Try again.')
          removeSessionItem('baba_auth_callback')
          setPendingMagicLink(null)
          setMagicLinkBusy(false)
          window.history.replaceState(null, '', cleanMagicLinkUrl(searchParams))
          setSearchParams(new URLSearchParams(window.location.search), { replace: true })
        }
      })
      .catch(() => {
        if (cancelled) return
        toast.error('Sign-in failed. Please try again.')
        removeSessionItem('baba_auth_callback')
        setPendingMagicLink(null)
        setMagicLinkBusy(false)
        window.history.replaceState(null, '', cleanMagicLinkUrl(searchParams))
        setSearchParams(new URLSearchParams(window.location.search), { replace: true })
      })

    return () => {
      cancelled = true
    }
  }

  const cancelMagicLink = () => {
    if (magicLinkBusy) return
    removeSessionItem('baba_auth_callback')
    setPendingMagicLink(null)
    window.history.replaceState(null, '', cleanMagicLinkUrl(searchParams))
    setSearchParams(new URLSearchParams(window.location.search), { replace: true })
  }

  // Step 1 — URL → durable marker. The return leg lands on
  // `?checkout=success&session_id=…`; record it in sessionStorage BEFORE the
  // params are stripped. The polling below is keyed on that marker, not on the
  // URL: stripping the params re-runs any effect that depends on searchParams
  // and fires its cleanup, which is exactly how the previous implementation
  // cancelled its own timer before the first poll (review finding, 2026-08-30).
  const returnTrackedRef = useRef(false)
  useEffect(() => {
    const checkout = searchParams.get('checkout')
    if (checkout === 'success') {
      const sessionId = searchParams.get('session_id')
      // The return is reported HERE, before the params are stripped below.
      // It used to fire from App, whose effect runs after this one and so
      // always found the params already gone. The purchase itself is the
      // Stripe webhook's to report (trackCheckoutReturned says why). The ref
      // keeps a re-run (the strip itself, StrictMode) from reporting it twice.
      if (!returnTrackedRef.current) {
        returnTrackedRef.current = true
        const plan = readSessionItem('baba.checkout.plan') === 'yearly' ? 'yearly' : 'monthly'
        const trial = readSessionItem('baba.checkout.trial') === 'true'
        // Stripe's {CHECKOUT_SESSION_ID} is the webhook's transaction id too,
        // which is what joins these markers to the server-side purchase.
        trackCheckoutReturned({ plan, trial, transactionId: sessionId && !sessionId.startsWith('{') ? sessionId : undefined })
        removeSessionItem('baba.checkout.plan')
        removeSessionItem('baba.checkout.trial')
      }
      writeSessionItem(CHECKOUT_PENDING_KEY, sessionId || '1')
      setCheckoutPending(true)
      const next = new URLSearchParams(searchParams)
      next.delete('checkout')
      next.delete('session_id')
      setSearchParams(next, { replace: true })
    } else if (checkout === 'canceled') {
      toast.info('Checkout canceled. You can try again anytime.')
      const next = new URLSearchParams(searchParams)
      next.delete('checkout')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Step 2 — activation polling, driven by the marker. Activation is
  // asynchronous: Stripe redirects the buyer here while the webhook that writes
  // the entitlement is still in flight. Poll on a back-off schedule for about
  // two minutes, self-heal via /restore after the first misses, tell the user
  // what is happening, then keep checking quietly. The marker survives a
  // reload, so "reload this page" restarts the loop instead of losing it; it is
  // cleared only once the subscription is seen active.
  useEffect(() => {
    if (!checkoutPending) return
    if (readSessionItem(CHECKOUT_PENDING_KEY) === null) return
    let cancelled = false
    // A silent update reload in a background tab would drop this wait.
    const releaseBusy = markAppBusy('checkout')
    const TOAST_ID = 'checkout-activation'
    const schedule = [1000, 1500, 2000, 3000, 4000, 6000, 8000, 10000, 12000, 15000, 15000, 15000, 15000, 15000]
    const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))
    const fetchStatus = () =>
      dispatch(babaApi.endpoints.subscriptionStatus.initiate(undefined, { forceRefetch: true }))
        .unwrap()
        .catch(() => ({ status: 'free' as const, plan: null }))

    const activate = async (status: Awaited<ReturnType<typeof fetchStatus>>) => {
      removeSessionItem(CHECKOUT_PENDING_KEY)
      broadcastSubscriptionStatus(status)
      writeSessionItem(CHECKOUT_TOAST_KEY, '1')
      // The gateway enforces its own copy of the plan (characters, images,
      // PDFs, voice, the tutor). Copy the new subscription across before the
      // reload, bounded so the reload never waits on it for long; the next
      // load reconciles again if this did not finish (GatewayIdentitySync).
      await Promise.race([syncGatewayEntitlement(), sleep(5000)])
      if (cancelled) return
      toast.dismiss(TOAST_ID)
      window.location.reload()
    }

    const run = async () => {
      let restoreAttempts = 0
      for (let i = 0; i < schedule.length; i++) {
        await sleep(schedule[i])
        if (cancelled) return
        let status = await fetchStatus()
        if (cancelled) return
        if (isSubscriptionActive(status)) return activate(status)

        // After the first two misses, ask the API to re-read Stripe directly
        // (covers a dropped or delayed webhook). Retry restore a few times
        // across the window rather than once.
        if (i >= 1 && restoreAttempts < 4 && (i - 1) % 3 === 0) {
          restoreAttempts += 1
          try {
            const restored = await restoreSubscription().unwrap()
            if (cancelled) return
            if (restored.restored || restored.already_active) {
              status = await fetchStatus()
              if (cancelled) return
              if (isSubscriptionActive(status)) return activate(status)
            }
          } catch {}
        }

        if (i === 2) {
          toast.loading('Payment received: activating your Pro plan…', { id: TOAST_ID, duration: Infinity })
        }
      }
      if (cancelled) return
      toast.error(
        'Your payment went through, but activation is taking longer than usual. We keep checking in the background. If Pro does not appear within a few minutes, reload this page or email shalom@itsbaba.com.',
        { id: TOAST_ID, duration: 20000 },
      )
      // Background tail: every 20s for another 5 minutes.
      for (let j = 0; j < 15; j++) {
        await sleep(20000)
        if (cancelled) return
        const status = await fetchStatus()
        if (cancelled) return
        if (isSubscriptionActive(status)) return activate(status)
      }
      // Give up for this page load but leave the marker: a reload retries.
      setCheckoutPending(false)
    }
    void run()
    return () => {
      cancelled = true
      releaseBusy()
    }
  }, [checkoutPending, dispatch, restoreSubscription])

  return (
    <SignInProvider>
    <PaywallProvider>
      {/*
       * Shell structure: icon rail + main content area. Pages fill <main>
       * edge to edge — no outer card framing. On mobile the rail is hidden
       * and a bottom tab bar replaces it — no hamburger, no drawer.
       */}
      <div className="app-shell flex flex-col md:flex-row overflow-hidden bg-app text-app">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold focus:bg-surface focus:text-app focus:shadow-token-md focus:outline-none"
        >
          Skip to main content
        </a>
        <Rail />
        {/* data-private: PostHog replay masks every character inside <main>
            (see maskTextSelector in lib/posthog.ts). Replay is started on the
            paywall and onboarding, which render outside <main>, so those
            flows stay readable while what people translate, ask or practise
            never leaves the browser. */}
        <main ref={mainRef} id="main" className="flex-1 min-w-0 flex flex-col overflow-hidden" data-private>
          {isSignedIn() && <GatewayIdentitySync />}
          <MobileAppPromo />
          <PhoneLinkPrompt />
          {pendingSession ? (
            <SignInConfirmDialog
              email={pendingSession.email}
              busy={false}
              onConfirm={() => adoptSession(pendingSession)}
              onCancel={() => setPendingSession(null)}
            />
          ) : pendingMagicLink ? (
            <SignInConfirmDialog
              email={pendingMagicLink.email}
              busy={magicLinkBusy}
              onConfirm={confirmMagicLink}
              onCancel={cancelMagicLink}
            />
          ) : null}
          <RouteTransition>
            <Outlet />
          </RouteTransition>
        </main>
        <MobileTabBar />
      </div>
    </PaywallProvider>
    </SignInProvider>
  )
}
